import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AppointmentService from "../../api/AppointmentService";
import JobService, { JobQueryParams } from "../../api/JobService";
import PropertyService from "../../api/PropertyService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomTextArea } from "../../components/CustomTextArea/CustomTextArea";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import { MaterialsList } from "../../components/MaterialsList/MaterialsList";
import NoProductDocumentsModal from "../../components/Modal/NoProductDocumentsModal";
import { PropertyAreaMonitors } from "../../components/PropertyAreaMonitors/PropertyAreaMonitors";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { goOneStepBack } from "../../services/NavigationService";
import { RootState } from "../../store";
import { appStateActions } from "../../store/app-state-slice";
import { checkIsRunning } from "../../store/clock-slice";
import { creatingDocumentationActions } from "../../store/documentation-slice";
import styles from "../../styles/services-overview.module.css";
import { calculateTotalTime } from "../../utils/calculateTotalTime";
import { mapPropertyAreasWithMonitors } from "../../utils/mapPropertyAreas";

export const SecondServicesOverview = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [jobId, setJobId] = useState("");
  const [propertyAreaMonitors, setPropertyAreaMonitors] = useState<any[]>([]);
  const [products, setProducts] = useState<any>(null);
  const [webProductsPDF, setWebProductsPDF] = useState<any>(null);
  const [sevdeskProductsPDF, setSevdeskProductsPDF] = useState<any>(null);
  const [infestationCountPerArea, setInfestationCountPerArea] = useState<{
    [key: string]: number;
  }>({});
  const [loading, setLoading] = useState(true);

  const appointment = useSelector((state: RootState) => state.appointment);
  const user = useSelector((state: RootState) => state.user.currentUser.user);

  const serviceStarted = useSelector((state: RootState) =>
    checkIsRunning(state, appointment.id, user!.id)
  );
  const isOnline = useSelector((state: RootState) => state.online.isOnline);

  const documentation = useSelector((state: RootState) => state.documentation);

  const appointmentPropAreas = useSelector(
    (state: RootState) =>
      state.appointmentPropertyAreas.appointmentPropertyAreas
  );

  useEffect(() => {
    if (appointment.jobs.length > 0) {
      const copiedJobs = Array.from(appointment.jobs);
      const lastJobId = copiedJobs.pop();
      setJobId(lastJobId.id);
    }
  }, [appointment.jobs]);

  const fetchPropertyAreaMonitors = useCallback(async () => {
    if (appointment.propertyId && appointment.id) {
      try {
        const [propertyResponse, propertyAreasResponse] = await Promise.all([
          PropertyService.GetProperty(appointment.propertyId),
          AppointmentService.GetAppointmentPropertyAreasWithVersions(
            appointment.id
          ),
        ]);

        const property = propertyResponse.data;
        const propertyAreasWithVersions = propertyAreasResponse.data;

        dispatch(appStateActions.addProperty(property));
        const propertyAreas = (propertyAreasWithVersions || []).filter(
          (area: any) =>
            Array.isArray(area.monitors) && area.monitors.length > 0
        );
        const propertyAreaIds = propertyAreas.map((area: any) => area.id);

        const monitorsPromises = propertyAreaIds.map((propertyAreaId: any) =>
          AppointmentService.GetMonitorsWithVersions(
            appointment.id,
            propertyAreaId
          )
        );

        const monitorsResponses = await Promise.all(monitorsPromises);

        const monitorsData = monitorsResponses.map((response) =>
          response.data.filter(
            (monitor: any) => monitor.versions && monitor.versions.length > 0
          )
        );

        setPropertyAreaMonitors(
          mapPropertyAreasWithMonitors(propertyAreas, monitorsData)
        );

        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    }
  }, [dispatch, appointment.propertyId, appointment.id]);

  const fetchGroupedMaterialsByJobPDF = useCallback(async () => {
    if (jobId) {
      const { data } = await JobService.GetGroupedMaterialsByJobPDF(jobId);
      if (data.webProducts) {
        setWebProductsPDF(
          data.webProducts.map((p: any) => ({
            id: p.item.productId,
            name: p.item.product.name,
            quantity: p.quantity,
            description: p.item.product.description,
            payExtraQuantity: p.payExtraQuantity ?? 0,
          }))
        );
      }

      if (data.sevdeskProducts) {
        setSevdeskProductsPDF(
          data.sevdeskProducts.map((p: any) => ({
            id: p.item.id,
            name: p.item.name,
            quantity: p.quantity,
            description: p.item.description,
            payExtraQuantity: p.payExtraQuantity ?? 0,
          }))
        );
      }
    }
  }, [jobId]);

  const fetchGroupedEventsByJob = useCallback(async () => {
    if (jobId) {
      try {
        const { data } = await JobService.GetGroupedEventsByJob(jobId);

        const infestationCountMap: { [key: string]: number } = {};

        data.forEach((areaData: any) => {
          const areaLabel = areaData.label;
          const infestationEvents = areaData.events.infestation;

          if (infestationEvents) {
            infestationCountMap[areaLabel] = infestationEvents.length;
          } else {
            infestationCountMap[areaLabel] = 0;
          }
        });

        setInfestationCountPerArea(infestationCountMap);
      } catch (error) {
        console.log(error);
      }
    }
  }, [jobId]);

  const fetchGroupedDeficienciesPerPropertyAreaByJob = useCallback(async () => {
    if (jobId) {
      const { firstEvent, lastEvent } = await calculateTotalTime(
        appointment.id
      );

      if (firstEvent && lastEvent) {
        const newQueryParams: JobQueryParams = {
          "filter[sinceDate]": moment(firstEvent.createdAt).format(
            "YYYY-MM-DDTHH:mm:ss"
          ),
          "filter[untilDate]": moment(lastEvent.createdAt).format(
            "YYYY-MM-DDTHH:mm:ss"
          ),
        };

        try {
          const { data } =
            await JobService.GetGroupedDeficienciesPerPropertyAreaByJob(
              jobId,
              newQueryParams
            );

          const mappedData = data.map((item: any) => {
            const match = appointmentPropAreas.find(
              (apa) => apa.id === item.propertyAreaId
            );

            return {
              ...item,
              area: match?.label ?? item.area,
            };
          });

          dispatch(
            creatingDocumentationActions.setPropertyAreaDeficiencies(mappedData)
          );
        } catch (error) {
          console.log(error);
        }
      }
    }
  }, [dispatch, jobId, appointmentPropAreas]);

  const fetchGroupedEventsPerPropertyAreaByJob = useCallback(async () => {
    if (jobId) {
      try {
        const { data } =
          await JobService.GetGroupedEventsPerPropertyAreaByJob(jobId);

        const mappedData = data.map((item: any) => {
          const match = appointmentPropAreas.find(
            (apa) => apa.id === item.propertyAreaId
          );

          return {
            ...item,
            area: match?.label ?? item.area,
          };
        });

        dispatch(
          creatingDocumentationActions.setPropertyAreaEvents(mappedData)
        );
      } catch (error) {
        console.log(error);
      }
    }
  }, [jobId, appointmentPropAreas]);

  const fetchGroupedMonitorsPerPropertyAreaByAppointment =
    useCallback(async () => {
      if (appointment.id) {
        try {
          const { data } = await AppointmentService.GetAdjustedMonitors(
            appointment.id
          );

          dispatch(
            creatingDocumentationActions.setPropertyAreaAdjustedMonitors(data)
          );
        } catch (error) {
          console.log(error);
        }
      }
    }, [appointment.id]);

  useEffect(() => {
    if (isOnline) {
      fetchPropertyAreaMonitors();
      fetchGroupedMaterialsByJobPDF();
      fetchGroupedEventsByJob();
      fetchGroupedEventsPerPropertyAreaByJob();
      fetchGroupedDeficienciesPerPropertyAreaByJob();
      fetchGroupedMonitorsPerPropertyAreaByAppointment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchGroupedEventsByJob,
    fetchPropertyAreaMonitors,
    fetchGroupedEventsPerPropertyAreaByJob,
    fetchGroupedDeficienciesPerPropertyAreaByJob,
    fetchGroupedMonitorsPerPropertyAreaByAppointment,
    fetchGroupedMaterialsByJobPDF,
    isOnline,
    appointment,
  ]);

  useEffect(() => {
    const combinedProductsPDF = [
      ...(webProductsPDF || []),
      ...(sevdeskProductsPDF || []),
    ];

    setProducts(combinedProductsPDF);
    dispatch(creatingDocumentationActions.setProducts(combinedProductsPDF));
  }, [webProductsPDF, sevdeskProductsPDF, dispatch]);

  const handleBackButtonClicked = () => {
    goOneStepBack(navigate);
  };

  const handleContinueClick = () => {
    navigate(`/services/${appointment.id}/services-completion`);
  };

  const propertyAreaMonitorsSortedByInfestationCount = useMemo(() => {
    return [...propertyAreaMonitors].sort((a, b) => {
      const infestationCountA = infestationCountPerArea[a.propertyArea] || 0;
      const infestationCountB = infestationCountPerArea[b.propertyArea] || 0;
      return infestationCountB - infestationCountA;
    });
  }, [propertyAreaMonitors, infestationCountPerArea]);

  return (
    <AppLayout hideHeader>
      <div>
        <NoProductDocumentsModal />

        <BackButtonHeader
          handleBackButtonClicked={handleBackButtonClicked}
          showDocumentation={false}
        />

        <AppointmentDetails isServicesOverview={true} />
        {loading && <LoadingSpinner loading={loading} />}
        <div className={styles["content-wrapper"]}>
          {!loading && (
            <>
              <div
                className={styles["big-title"]}
                style={{ marginBottom: "24px" }}
              >
                {t("MaterialsAndPreparations")}
              </div>

              <MaterialsList
                title={"Materialeinsatz"}
                data={products}
                readOnly={true}
              />

              <div
                className={styles["big-title"]}
                style={{ marginTop: "16px", marginBottom: "14px" }}
              >
                {t("Remark")}
              </div>

              <CustomTextArea
                placeholder={t("AdditionalText")}
                defaultValue={documentation.remark ?? ""}
                onTextareaChange={() => {}}
                readonly
              />

              <div
                className={styles["radio-section"]}
                style={{ marginTop: "10px" }}
              >
                <span>{t("RoomClearance")}</span>

                <div
                  className={styles["radio-group--label"]}
                  style={{ marginTop: "24px" }}
                >
                  <input type="radio" checked readOnly />
                  <span>{documentation.roomRelease || "-"}</span>
                </div>
              </div>

              <div
                className={styles["radio-section"]}
                style={{ marginTop: "16px" }}
              >
                <span>{t("MeasuresRequired")}</span>

                <div
                  className={styles["radio-group--label"]}
                  style={{ marginTop: "24px" }}
                >
                  <input type="radio" checked readOnly />
                  <span>{documentation.measureRequired || "-"}</span>
                </div>
              </div>

              <div
                className={styles["big-title"]}
                style={{ marginTop: "16px", marginBottom: "16px" }}
              >
                {t("Monitors")}
              </div>

              <div className={styles.monitors}>
                <PropertyAreaMonitors
                  monitors={propertyAreaMonitorsSortedByInfestationCount}
                  startService={() => {}}
                  isServiceStarted={serviceStarted}
                  isServicesOverview={true}
                  jobId={jobId}
                  infestationCountPerArea={infestationCountPerArea}
                />
              </div>
            </>
          )}

          <FloatingButtons>
            <CustomButton
              color="green"
              width={80}
              onClick={handleContinueClick}
            >
              {t("ConfirmText")}
            </CustomButton>
          </FloatingButtons>
        </div>
      </div>
    </AppLayout>
  );
};
